import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarCheck, Settings2 } from "lucide-react";
import { buildWeeks, useGrow, toInput } from "../lib/irrigation";
import { fmtDate } from "../lib/plan";
import { scrollToId } from "../lib/api";
import { FILTERS } from "../components/cuadrante/cq";
import { CqDay } from "../components/cuadrante/CqDay";
import { CqWeekStrip } from "../components/cuadrante/CqWeekStrip";
import { CqTable } from "../components/cuadrante/CqTable";

const locate = (weeks) => {
  const t = toInput(new Date());
  for (let w = 0; w < weeks.length; w += 1) {
    const d = weeks[w].days.findIndex((x) => toInput(x.date) === t);
    if (d >= 0) return { w, d, where: "in" };
  }
  return toInput(weeks[0].days[0].date) > t ? { w: 0, d: 0, where: "before" } : { w: weeks.length - 1, d: 6, where: "after" };
};

const Chip = ({ id, label, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`cq-filter-${id}`} aria-pressed={active}
    className={`shrink-0 h-10 px-4 rounded-full border-2 text-sm font-medium transition-[background-color,border-color] ${active ? "bg-white text-[#0F3D1E] border-white" : "bg-[#0F3D1E]/80 text-white border-[#8CD05A]/40"}`}>
    {label}
  </button>
);

export default function Cuadrante() {
  const [cfg] = useGrow();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const weeks = useMemo(() => buildWeeks(cfg), [cfg]);
  const here = useMemo(() => locate(weeks), [weeks]);
  const [w, setW] = useState(here.w);
  const [d, setD] = useState(here.d);
  const filter = FILTERS.find(([id]) => id === params.get("producto")) ?? FILTERS[0];
  const week = weeks[w] ?? weeks[0];

  useEffect(() => { setW(here.w); setD(here.d); }, [here]);
  useEffect(() => {
    document.title = "Cuadrante de riego · Nutrient Jungle";
    return () => { document.title = "Jungle Nutrient — Biotecnología optimizada por IA"; };
  }, []);

  const goWeek = (i) => { setW(Math.max(0, Math.min(weeks.length - 1, i))); setD(0); };
  const goToday = () => { setW(here.w); setD(here.d); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const pick = (i) => { goWeek(i); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const setFilter = (id) => setParams(id === "todos" ? {} : { producto: id }, { replace: true });
  const adjust = () => { navigate("/"); setTimeout(() => scrollToId("mi-cultivo"), 700); };

  return (
    <main data-testid="cuadrante-page" className="relative min-h-screen pt-[88px] pb-32 text-white">
      <div aria-hidden className="fixed inset-0 z-0 pointer-events-none">
        <img src="/img/jungle-hero-bg.webp" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#06170C]/70" />
      </div>
      <div className="relative z-10 max-w-[560px] mx-auto px-4">
        <p className="font-mono-d text-xs tracking-[0.2em] uppercase text-[#B9E79A]">Cuadrante de riego · 100 L</p>
        <h1 className="font-serif-d font-black text-4xl sm:text-5xl leading-[0.95] mt-2">Tu riego de hoy</h1>
        {here.where !== "in" && (
          <p data-testid="cq-out-of-plan" className="mt-3 rounded-[14px] bg-[#0F3D1E]/90 border border-[#F2B441]/60 p-3 text-sm">
            {here.where === "before" ? `Tu cultivo empieza el ${fmtDate(weeks[0].days[0].date, { day: "numeric", month: "long" })}.` : "Tu cultivo ya ha terminado. Ajusta una nueva fecha de inicio."}
          </p>
        )}
        <div data-lenis-prevent className="mt-5 -mx-4 px-4 flex gap-2 overflow-x-auto pb-1" data-testid="cq-filters">
          {FILTERS.map(([id, label]) => <Chip key={id} id={id} label={label} active={filter[0] === id} onClick={() => setFilter(id)} />)}
        </div>
        <div className="mt-5"><CqWeekStrip week={week} sel={d} onSel={setD} /></div>
        <div className="mt-4"><CqDay day={week.days[d]} week={week} focus={filter[2]} focusLabel={filter[1]} /></div>
        <button type="button" onClick={adjust} data-testid="cq-adjust" className="mt-4 inline-flex items-center gap-2 text-sm text-[#E6F2DF] underline underline-offset-4">
          <Settings2 className="w-4 h-4" aria-hidden /> Ajustar fecha de inicio, genética y días de riego
        </button>
        <CqTable weeks={weeks} current={here.where === "in" ? here.w : -1} active={w} focus={filter[2]} onPick={pick} />
        <p className="text-xs text-[#E6F2DF] mt-5">Mide la EC del agua de partida (tabla calculada con EC 0,6). PK y Compact pueden aplicarse juntos. Funciona sin conexión una vez abierta.</p>
      </div>
      <nav data-testid="cq-bottom-bar" aria-label="Navegar semanas" className="fixed bottom-0 inset-x-0 z-40 bg-[#0F3D1E]/95 backdrop-blur-xl border-t border-[#8CD05A]/30 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-[560px] mx-auto px-3 h-[68px] grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <button type="button" onClick={() => goWeek(w - 1)} disabled={w === 0} data-testid="cq-prev-week" className="h-12 rounded-full flex items-center justify-center gap-1 text-white disabled:opacity-30">
            <ChevronLeft className="w-5 h-5" aria-hidden /> Anterior
          </button>
          <button type="button" onClick={goToday} data-testid="cq-go-today" className="h-12 px-6 rounded-full bg-[#F2B441] text-[#0F3D1E] font-semibold flex items-center gap-2 active:scale-95 transition-transform">
            <CalendarCheck className="w-5 h-5" aria-hidden /> Hoy
          </button>
          <button type="button" onClick={() => goWeek(w + 1)} disabled={w === weeks.length - 1} data-testid="cq-next-week" className="h-12 rounded-full flex items-center justify-center gap-1 text-white disabled:opacity-30">
            Siguiente <ChevronRight className="w-5 h-5" aria-hidden />
          </button>
        </div>
      </nav>
    </main>
  );
}
